const BASE_URL = "https://hirecrap-backend-gcg3e8cmcgfwe6ef.westus2-01.azurewebsites.net/api/seo";

/**
 * Fetch SEO page data by slug
 * @param {string} slug - e.g. "software-developer-jobs-in-bangalore"
 * @returns {Promise<Object>} SEO page data
 */
export const getSEOPageBySlug = async (slug) => {
  const response = await fetch(`${BASE_URL}/${slug}`, {
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Failed to fetch SEO page: ${response.status}`);
  const json = await response.json();
  if (!json.success) throw new Error("SEO page API returned success: false");
  return json.data;
};

/**
 * Fetch all SEO listing pages with optional filters
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.limit=12]
 * @param {string} [params.location]
 * @param {string} [params.country]
 * @param {string} [params.search]
 * @returns {Promise<{ data: Object[], pagination: Object }>}
 */
export const getSEOPages = async ({
  page = 1,
  limit = 12,
  location = "",
  // country = "",
  search = "",
} = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    is_published: "true",
    sortBy: "createdAt",
    order: "desc",
    ...(location && { location }),
    // ...(country && { country }),
    ...(search && { search }),
  });
  const response = await fetch(`${BASE_URL}?${params.toString()}`, {
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Failed to fetch SEO pages: ${response.status}`);
  const json = await response.json();
  if (!json.success) throw new Error("SEO pages API returned success: false");
  return json;
};

/**
 * Fetch jobs based on title keyword, country and location
 * @param {Object} params
 * @param {string} params.title - job title keyword
 * @param {string} params.country - e.g. "India"
 * @param {string} params.location - e.g. "Bangalore"
 * @param {number} [params.page=1]
 * @param {number} [params.limit=20]
 * @returns {Promise<Object>} { total, page, totalPages, jobs }
 */
export const getJobsByLocation = async ({
  title = "",
  country = "",
  location = "",
  page = 1,
  limit = 20,
}) => {
  const params = new URLSearchParams({
    ...(title && { title }),
    ...(country && { country }),
    ...(location && { location }),
    page: String(page),
    limit: String(limit),
  });
  const response = await fetch(`${BASE_URL}/jobs?${params.toString()}`, {
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Failed to fetch jobs: ${response.status}`);
  return response.json();
};

/**
 * Fetch detailed job description by job ID
 * @param {string} jobId
 * @param {string} country - e.g. "India"
 * @returns {Promise<Object>} full job detail object
 */
export const getJobById = async (jobId, country = "") => {
  const params = new URLSearchParams({ ...(country && { country }) });
  const response = await fetch(
    `${BASE_URL}/jobById/${jobId}${params.toString() ? `?${params.toString()}` : ""}`,
    { headers: { accept: "*/*" } }
  );
  if (!response.ok) throw new Error(`Failed to fetch job detail: ${response.status}`);
  return response.json();
};

/**
 * Derive the primary keyword from an SEO page's keywords array or title.
 * Used as the `title` query param when fetching jobs.
 * @param {Object} seoData
 * @returns {string}
 */
export const deriveJobKeyword = (seoData) => {
  if (seoData?.keywords?.length > 0) return seoData.keywords[0];
  return seoData?.title?.split(" ").slice(0, 2).join(" ") || "";
};